import { useEffect } from "react";
import { Scan, LockOpen, VolumeX, Wrench, CreditCard, Banknote, MapPin, Mail, Linkedin, BarChart3, Users, ArrowRight, Wifi, Plug, Check, Box, PoundSterling, TrendingUp, CalendarCheck, RefreshCw } from "lucide-react";

import casestudy2 from "@/assets/deck/casestudy-2.jpg";
import casestudy3 from "@/assets/deck/casestudy-3.jpg";
import casestudy4 from "@/assets/deck/casestudy-4.jpg";
import podInSitu from "@/assets/deck/pod-in-situ.jpeg";
import tflLogo from "@/assets/deck/tfl-logo.png";
import qwipFounders from "@/assets/deck/qwip-founders.jpg";
import telephoneBox from "@/assets/deck/telephone-box-1926.png";
import qwipPodRender from "@/assets/deck/qwip-pod-render.png";
import timesArticle from "@/assets/deck/times-article-new.png";
import headshotZain from "@/assets/deck/headshot-zain.jpeg";
import headshotStiofan from "@/assets/deck/headshot-stiofan.png";
import headshotShreyas from "@/assets/deck/headshot-shreyas.png";
import logoMonzo from "@/assets/deck/logo-monzo-new.png";
import logoEF from "@/assets/deck/logo-ef-new.png";
import logoOlympics from "@/assets/deck/logo-olympics-new.png";
import logoAstraZeneca from "@/assets/deck/logo-astrazeneca-new.png";
import logoBloomberg from "@/assets/deck/logo-bloomberg.png";
import logoAmazon from "@/assets/deck/logo-amazon.png";
import slide1Bg from "@/assets/deck/slide1-bg.jpg";


const PDF_PAGE_WIDTH = "13.333in";
const PDF_PAGE_HEIGHT = "7.5in";

const Slide = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`slide-page w-full aspect-video relative overflow-hidden ${className}`}>
    {children}
  </div>
);

const QwipLogoMark = ({ light = false }: { light?: boolean }) => (
  <div className={`absolute top-6 right-6 font-funnel font-bold text-xl ${light ? "text-white" : "text-foreground"}`}>qwip</div>
);

const InvestorDeck = () => {
  useEffect(() => {
    const timer = setTimeout(() => window.print(), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{`
        @media print {
          @page { size: ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}; margin: 0; }
          html, body, #root {
            width: ${PDF_PAGE_WIDTH};
            margin: 0;
            padding: 0;
          }
          body {
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important; }
          .slide-page {
            width: ${PDF_PAGE_WIDTH} !important;
            height: ${PDF_PAGE_HEIGHT} !important;
            aspect-ratio: auto !important;
            overflow: hidden;
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: always;
            break-after: page;
          }
          .slide-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
        @media screen {
          body { margin: 0; }
          .slide-page { margin: 0 auto 2px; }
        }
      `}</style>

      {/* Print controls */}
      <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
        <button onClick={() => window.print()} className="bg-foreground text-primary-foreground px-4 py-2 rounded-lg font-sans text-sm font-medium hover:opacity-90">
          Save as PDF
        </button>
        <button onClick={() => window.close()} className="bg-foreground/10 text-foreground px-4 py-2 rounded-lg font-sans text-sm font-medium hover:bg-foreground/20">
          Close
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 1 — COVER
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col justify-between px-16 py-14 relative" style={{ backgroundImage: `url(${slide1Bg})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          <div className="absolute inset-0 bg-black/30" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" as any }} />
          <div className="relative z-10" />
          <div className="relative z-10">
            <h1 className="font-funnel font-bold text-[5.5rem] text-white leading-[0.95] tracking-tight max-w-5xl">
              the way we work<br />has changed.
            </h1>
            <p className="font-cormorant italic font-normal text-[3.2rem] text-white leading-[1.1] mt-4 max-w-4xl">
              but our cities haven't kept up.
            </p>
          </div>
          <div className="relative z-10">
            <p className="font-funnel font-bold text-3xl text-white mb-1">qwip</p>
            <p className="text-white/60 font-sans text-base">bookable private pods in public spaces.</p>
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 2 — PROBLEM
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-24 text-center" style={{ background: "linear-gradient(135deg, hsl(210 30% 12%), hsl(200 25% 16%), hsl(190 20% 14%))" }}>
          <QwipLogoMark light />
          <h2 className="font-funnel font-bold text-[4.5rem] text-white leading-[1] tracking-tight mb-6 max-w-5xl">
            300+ million people now work remotely worldwide.
          </h2>
          <p className="font-funnel text-[2.2rem] text-white/80 leading-[1.2] mb-8 max-w-4xl">
            but there's one problem <strong>every</strong> remote worker still faces.
          </p>
          <img src={timesArticle} alt="The Times — Is it acceptable to have a Zoom meeting in a café?" className="max-w-2xl w-full rounded-2xl border border-white/10 mb-10" />
          <p className="font-cormorant italic text-[2.8rem] text-white/90 leading-[1.15] max-w-4xl">
            where do you take that last-minute call?
          </p>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 3 — PHONE BOOTH COMPARISON
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-20">
          <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-12 text-center">
            the solution isn't new,{" "}
            <span className="font-cormorant italic font-normal">it's historic.</span>
          </h2>
          <div className="grid grid-cols-[1fr_auto_1fr] gap-12 items-end max-w-4xl w-full">
            <div className="flex flex-col items-center">
              <div className="w-64 h-80 rounded-3xl overflow-hidden mb-4">
                <img src={telephoneBox} alt="1926 telephone box" className="w-full h-full object-contain" />
              </div>
              <p className="font-funnel font-bold text-2xl text-foreground mb-1">the telephone box</p>
              <p className="text-muted-foreground font-sans text-lg">1926</p>
            </div>
            <div className="flex flex-col items-center pb-20">
              <div className="w-px h-48" style={{ background: "linear-gradient(to bottom, transparent, hsl(0 0% 70%), transparent)" }} />
            </div>
            <div className="flex flex-col items-center">
              <div className="w-64 h-80 rounded-3xl overflow-hidden mb-4">
                <img src={qwipPodRender} alt="2026 qwip pod" className="w-full h-full object-contain" />
              </div>
              <p className="font-funnel font-bold text-2xl text-foreground mb-1">the qwip pod</p>
              <p className="text-muted-foreground font-sans text-lg">2026</p>
            </div>
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 4 — SOLUTION
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="grid grid-cols-2 h-full">
          <div className="flex flex-col justify-center px-16 py-12">
            <p className="font-funnel font-bold text-2xl text-primary mb-4">qwip</p>
            <h2 className="font-funnel text-5xl text-foreground leading-[0.95] tracking-tight mb-8">
              we place <span className="font-bold text-primary">bookable, soundproof pods</span> in <span className="font-bold text-primary">public spaces.</span>
            </h2>
            <p className="text-lg text-muted-foreground font-funnel leading-relaxed max-w-lg">
              For <strong className="text-foreground">calls</strong>, <strong className="text-foreground">focused work</strong>, or a <strong className="text-foreground">quiet moment</strong>.
            </p>
          </div>
          <div className="flex items-center justify-center p-8">
            <img src={podInSitu} alt="qwip pod in a café" className="w-full h-full object-cover rounded-3xl max-h-[80vh]" />
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 5 — HOW IT WORKS
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="flex flex-col h-full">
          <div className="px-14 pt-16 pb-4">
            <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight">how it works:</h2>
          </div>
          <div className="flex-1 flex items-center px-14">
            <div className="w-full grid grid-cols-[1fr_1px_1fr] gap-x-12">
              <div className="mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the user</p>
              </div>
              <div />
              <div className="mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-sans font-bold">for the host</p>
              </div>
              {[
                { icon: Scan, title: "scan", desc: "user scans the QR code on the pod to start the booking process." },
                { icon: LockOpen, title: "unlock", desc: "they get an access code, enter it on the numpad, and unlock the pod." },
                { icon: VolumeX, title: "focus", desc: (<>the pod is all theirs. billed at <strong>$0.30/min</strong>, charged at the end of the session.</>) },
              ].map((step, i) => {
                const hostSteps = [
                  { icon: Wrench, title: "we install", desc: (<>We deliver and install the pod (1m × 1m) at <strong>zero cost</strong> to the host location.</>) },
                  { icon: CreditCard, title: "users pay", desc: "Users scan, book, and pay by the minute. We handle all payments and customer support." },
                  { icon: Banknote, title: "revenue share", desc: (<>We operate on a <strong>revenue share model</strong>. The host earns a percentage of every booking, with zero operational burden.</>) },
                ];
                const host = hostSteps[i];
                return (
                  <div key={step.title} className="contents">
                    <div className="flex gap-4 items-start p-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                        <step.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-funnel font-bold text-lg text-foreground mb-1">{step.title}</p>
                        <p className="text-muted-foreground text-sm font-sans leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                    <div className="bg-foreground/10" />
                    <div className="flex gap-4 items-start p-3">
                      <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-foreground text-primary-foreground flex items-center justify-center">
                        <host.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-funnel font-bold text-lg text-foreground mb-1">{host.title}</p>
                        <p className="text-muted-foreground text-sm font-sans leading-relaxed">{host.desc}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 5 — TRACTION (King's Cross)
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="grid grid-cols-2 h-full">
          <div className="flex flex-col justify-center px-16 py-6">
            <div className="flex items-center gap-6 mb-3">
              <span className="font-funnel font-bold text-[2.8rem] text-foreground">qwip</span>
              <div className="w-px h-14 bg-foreground/20" />
              <img src={tflLogo} alt="TfL" className="h-16 object-contain" />
            </div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="font-sans text-sm text-muted-foreground font-medium">King's Cross Station</span>
            </div>
            <p className="text-base text-muted-foreground font-funnel leading-relaxed max-w-lg mb-4">
              this january, we trialled our first pod at King's Cross Station, the busiest transport hub in the UK (<strong className="text-foreground">221,000 daily visitors</strong>).
            </p>
            <div className="flex gap-3 mb-4 max-w-lg">
              {[
                { value: "$140", label: "revenue in one day" },
                { value: "~60%", label: "utilisation rate" },
              ].map((item) => (
                <div key={item.label} className="flex-1 bg-foreground text-primary-foreground rounded-2xl p-4">
                  <p className="font-funnel font-bold text-2xl mb-1">{item.value}</p>
                  <p className="text-primary-foreground/70 font-sans text-xs">{item.label}</p>
                </div>
              ))}
            </div>
            <p className="font-funnel font-bold text-xl text-foreground mb-3"><strong>londoners want qwip.</strong></p>
            <div className="bg-foreground/5 border border-foreground/10 rounded-2xl p-5">
              <p className="font-sans text-foreground text-sm leading-relaxed mb-2">
                "I had an hour before my Eurostar train to Paris and <strong>nowhere private</strong> to take a <strong>client call</strong>. This was <strong>exactly what I needed</strong>."
              </p>
              <p className="font-sans text-xs text-muted-foreground font-semibold uppercase tracking-wider">Adnaan, Consultant</p>
            </div>
          </div>
          <div className="flex items-center justify-center p-6">
            <img src={qwipFounders} alt="qwip founders at King's Cross" className="w-full h-full object-cover rounded-3xl" />
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE — TEAM
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-20">
          <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-2 text-center">built by friends who've lived the problem</h2>
          <p className="font-cormorant italic font-medium text-[1.8rem] text-muted-foreground mb-10 text-center">and have shipped products and brands that have reached millions.</p>
          <div className="relative grid grid-cols-3 gap-10 max-w-5xl w-full">
            {/* Zain */}
            <div className="flex flex-col items-center text-center">
              <img src={headshotZain} alt="Zain Hussain" className="w-28 h-28 rounded-full object-cover mb-5" />
              <p className="font-funnel font-bold text-2xl text-foreground mb-0">Zain Hussain</p>
              <div className="flex items-center gap-2 mt-1 mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">CEO</p>
                <div className="w-px h-3 bg-foreground/20" />
                <a href="https://www.linkedin.com/in/zain-hussain" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
              </div>
              <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
                <p className="text-muted-foreground font-sans text-sm">Second-time founder</p>
                <p className="text-muted-foreground font-sans text-sm">Previously backed by Entrepreneur First, angels from a16z</p>
                <p className="text-muted-foreground font-sans text-sm">Early employee at Monzo (4+ yrs), led scam prevention ops (300+ team)</p>
                <p className="text-muted-foreground font-sans text-sm">Lawyer. Research adopted by the Law Commission of England & Wales.</p>
              </div>
              <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
                <img src={logoMonzo} alt="Monzo" className="h-10 object-contain opacity-60" />
                <img src={logoEF} alt="Entrepreneur First" className="h-10 object-contain opacity-60" />
              </div>
            </div>
            {/* Stiofán */}
            <div className="flex flex-col items-center text-center">
              <img src={headshotStiofan} alt="Stiofán Folan-Hasici" className="w-28 h-28 rounded-full object-cover mb-5" />
              <p className="font-funnel font-bold text-2xl text-foreground mb-0">Stiofán Folan-Hasici</p>
              <div className="flex items-center gap-2 mt-1 mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">COO</p>
                <div className="w-px h-3 bg-foreground/20" />
                <a href="https://www.linkedin.com/in/stiofanfh/" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
              </div>
              <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
                <p className="text-muted-foreground font-sans text-sm">Designer and brand strategist</p>
                <p className="text-muted-foreground font-sans text-sm">Built brands for Olympic athletes and AstraZeneca</p>
                <p className="text-muted-foreground font-sans text-sm">Grew brands from 0 to 1M+ followers and 2,300+ paid community members in under a year</p>
              </div>
              <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
                <img src={logoOlympics} alt="Olympics" className="h-10 object-contain opacity-60" />
                <img src={logoAstraZeneca} alt="AstraZeneca" className="h-10 object-contain opacity-60" />
              </div>
            </div>
            {/* Shreyas */}
            <div className="flex flex-col items-center text-center">
              <img src={headshotShreyas} alt="Shreyas Saxena" className="w-28 h-28 rounded-full object-cover mb-5" />
              <p className="font-funnel font-bold text-2xl text-foreground mb-0">Shreyas Saxena</p>
              <div className="flex items-center gap-2 mt-1 mb-4">
                <p className="text-xs uppercase tracking-[0.25em] text-primary font-sans font-bold">CTO</p>
                <div className="w-px h-3 bg-foreground/20" />
                <a href="#" target="_blank" rel="noopener noreferrer" className="text-foreground/40 hover:text-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
              </div>
              <div className="flex flex-col gap-1.5 mb-4 text-left w-full">
                <p className="text-muted-foreground font-sans text-sm">Engineer at Bloomberg, ex-Amazon</p>
                <p className="text-muted-foreground font-sans text-sm">Built financial data products used by top-tier institutions globally</p>
                <p className="text-muted-foreground font-sans text-sm">Replaced legacy Bloomberg Terminal workflows, cutting friction by 50%</p>
              </div>
              <div className="h-14 flex items-center justify-center gap-5 mt-auto pt-3">
                <img src={logoBloomberg} alt="Bloomberg" className="h-10 object-contain opacity-60" />
                <img src={logoAmazon} alt="Amazon" className="h-10 object-contain opacity-60" />
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 6 — MARKET VALIDATION
          ═══════════════════════════════════════════════════════════ */}
      <Slide className="bg-primary">
        <div className="flex flex-col h-full px-16 py-12 justify-center">
          <QwipLogoMark light />
          <h2 className="font-funnel font-bold text-5xl text-white leading-[0.95] tracking-tight mb-2">
            the world is arriving at the same answer.
          </h2>
          <p className="font-cormorant italic font-normal text-4xl text-white/80 mb-12">we're connecting the dots.</p>
          <div className="grid grid-cols-3 gap-8">
            {[
              { img: casestudy2, country: "Japan", stat: "400+", description: "booths deployed across Japanese railway stations" },
              { img: casestudy3, country: "United States", stat: "50+", description: "booths across hotel lobbies and airports" },
              { img: casestudy4, country: "Middle East", stat: "30+", description: "booths across shopping centres, cafés, and fitness clubs" },
            ].map((item) => (
              <div key={item.country}>
                <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-3">
                  <img src={item.img} alt={item.country} className="w-full h-full object-cover" />
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-white/60" />
                  <p className="font-funnel font-bold text-white text-lg">{item.country}</p>
                </div>
                <p className="text-white/70 text-sm font-funnel leading-relaxed">{item.stat} {item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 8 — UNIT ECONOMICS
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-20" style={{ background: "linear-gradient(135deg, hsl(158 33% 14%), hsl(158 33% 22%))" }}>
          <QwipLogoMark light />
          <h2 className="font-funnel font-bold text-5xl text-white tracking-tight mb-12 text-center">the pods pay for themselves.</h2>
          <div className="grid grid-cols-4 gap-6 max-w-5xl w-full">
            {[
              { value: "£180/mo", label: "total pod cost", icon: PoundSterling },
              { value: "£3,500/mo", label: "projected revenue\nper pod", icon: TrendingUp },
              { value: "one booking", label: "needed per day\nto break even", icon: CalendarCheck, prefix: "minimum" },
              { value: "~20x", label: "return on\nmonthly cost", icon: RefreshCw },
            ].map((item) => (
              <div key={item.label} className="bg-white text-foreground rounded-3xl p-8 flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                {item.prefix && <p className="text-foreground/60 font-sans text-sm font-medium leading-snug">{item.prefix}</p>}
                <p className="font-funnel font-bold text-3xl mb-2">{item.value}</p>
                <p className="text-foreground/60 font-sans text-sm font-medium whitespace-pre-line leading-snug">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 9 — REVENUE STREAMS
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-20">
          <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-12 text-center">three ways every pod makes money.</h2>
          <div className="grid grid-cols-3 gap-8 max-w-5xl w-full">
            {[
              {
                title: "Per-minute bookings",
                desc: "Core revenue. Users pay $0.30/min for private use. Billed automatically at the end of each session.",
                tag: "primary revenue",
                icon: CreditCard,
              },
              {
                title: "Brand placements on pods",
                desc: (<>External screen and interior branding placements. Already received interest from <strong className="text-foreground">Deel</strong>.</>),
                tag: "advertising",
                icon: BarChart3,
              },
              {
                title: "Subscription tier",
                desc: "Monthly pass for frequent users. Discounted rate and special perks.",
                tag: "recurring",
                icon: Users,
              },
            ].map((item) => (
              <div key={item.title} className="border border-foreground/10 rounded-3xl p-8 flex flex-col">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-sans font-bold mb-2">{item.tag}</p>
                <p className="font-funnel font-bold text-2xl text-foreground mb-3">{item.title}</p>
                <p className="text-muted-foreground font-sans text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 12 — ROADMAP
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center px-20">
          <h2 className="font-funnel font-bold text-5xl text-foreground tracking-tight mb-2 text-center">the platform to book any pod, anywhere.</h2>
          <p className="font-cormorant italic font-normal text-[2.2rem] text-muted-foreground mb-14 text-center">here's how we get there.</p>
          <div className="relative max-w-6xl w-full">
            <div className="absolute top-6 left-0 right-0 h-0.5 bg-foreground/10" />
            <div className="grid grid-cols-4 gap-6">
              {[
                { quarter: "Q1 2026", title: "Launch and pilot.", desc: "Pilot pod deployment at King's Cross with TfL.", active: true },
                { quarter: "Q2 2026", title: "10 pods across London.", desc: "10 permanent installations across London. Transit hubs, fitness clubs, cafes.", active: false },
                { quarter: "Q3 2026", title: "Expand beyond London.", desc: "50+ pods across the UK. Transit hubs, fitness clubs, and shopping centres.", active: false },
                { quarter: "Q4 2026", title: "Expand internationally.", desc: (<>International expansion to <strong className="text-foreground">San Francisco</strong>, <strong className="text-foreground">New York City</strong> and beyond.</>), active: false },
              ].map((item) => (
                <div key={item.quarter} className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 ${
                    item.active ? "bg-primary text-white" : "bg-foreground/10 text-foreground/40"
                  }`}>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                  <p className={`text-xs uppercase tracking-[0.25em] font-sans font-bold mb-2 ${item.active ? "text-primary" : "text-muted-foreground"}`}>{item.quarter}</p>
                  <p className="font-funnel font-bold text-lg text-foreground mb-2">{item.title}</p>
                  <p className="text-muted-foreground font-sans text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 11 — VISION
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col justify-center px-20 py-12" style={{ background: "linear-gradient(135deg, hsl(158 33% 12%), hsl(158 33% 18%), hsl(158 20% 22%))" }}>
          <QwipLogoMark light />
          <h2 className="font-funnel font-bold text-[5.5rem] text-white tracking-tight leading-[0.95] mb-3">
            every space will have a pod.
          </h2>
          <p className="font-cormorant italic font-normal text-[4rem] text-white/90 leading-[1] mb-14">
            the question is <span className="underline">whose</span>?
          </p>
          <div className="flex items-center gap-4 mb-14 flex-wrap">
            {[
              { icon: Wifi, label: "WiFi", highlight: false },
              { icon: Plug, label: "Power outlets", highlight: false },
              { icon: Box, label: "Soundproof Pod", highlight: true },
            ].map((amenity) => (
              <div
                key={amenity.label}
                className={`flex items-center gap-2.5 rounded-xl px-4 py-3 ${
                  amenity.highlight
                    ? "bg-white text-foreground border-2 border-white"
                    : "bg-white/10 text-white/70 border border-white/10"
                }`}
              >
                <Check className={`w-4 h-4 flex-shrink-0 ${amenity.highlight ? "text-primary" : "text-white/50"}`} />
                <amenity.icon className={`w-4 h-4 ${amenity.highlight ? "text-primary" : "text-white/50"}`} />
                <span className={`font-sans text-sm font-medium ${amenity.highlight ? "text-foreground font-bold" : ""}`}>{amenity.label}</span>
              </div>
            ))}
          </div>
          <p className="text-white/80 font-funnel text-[2.5rem] leading-snug max-w-5xl">
            Bookable pods are becoming part of standard infrastructure.
            <br /><strong className="text-white font-bold">qwip is building the network to own them, worldwide.</strong>
          </p>
        </div>
      </Slide>

      {/* ═══════════════════════════════════════════════════════════
          SLIDE 12 — CLOSE
          ═══════════════════════════════════════════════════════════ */}
      <Slide>
        <div className="h-full flex flex-col items-center justify-center text-center px-24" style={{ background: "linear-gradient(135deg, hsl(158 33% 14%), hsl(158 33% 22%), hsl(158 20% 28%))" }}>
          <p className="font-funnel font-bold text-9xl text-white mb-6">qwip</p>
          <p className="text-2xl text-white/70 font-funnel mb-10">the public phone booth, upgraded.</p>
          <div className="flex items-center gap-3 text-white/80 font-sans text-lg">
            <Mail className="w-5 h-5" />
            hello@qwip.co.uk
          </div>
        </div>
      </Slide>
    </>
  );
};

export default InvestorDeck;
